<template>
  <div class="widget-editor-layout">
    <h1 class="sr-only">Edit {{ item_label }}</h1>

    <div class="settings-pane" v-show="!settings_collapsed">
      <slot name="header-actions">
        <widget-editor-action-buttons
          :back_route="back_route"
          :back_label="back_label"
          :is_published="is_published"
          :is_archived="is_archived"
          :can_unarchive="can_unarchive"
          :unarchive_tooltip="unarchive_tooltip"
          :show_copy_url="show_copy_url"
          :show_qr_code="show_qr_code"
          :show_archive="show_archive"
          @copy-url="handleCopyUrl"
          @generate-qr="handleGenerateQR"
          @archive="handleShowArchiveDialog"
          @unarchive="handleUnarchive"
        >
          <template #custom-actions>
            <slot name="custom-actions" />
          </template>
        </widget-editor-action-buttons>
      </slot>

      <slot name="publication-status">
        <widget-editor-publication-status
          ref="publicationStatus"
          :is_published="is_published"
          :has_draft_changes="has_draft_changes"
          :is_archived="is_archived"
          :publish_time="publish_time"
          :publish_signature_id="publish_signature_id"
          :change_descriptions="change_descriptions"
          :validation_warnings="validation_warnings"
          :publish_dialog_title="publish_dialog_title"
          :include_setup_checklist="include_setup_checklist"
          :outstanding_steps_count="outstanding_steps_count"
          @publish="$emit('publish', $event)"
          @discard-changes="$emit('discard-changes', $event)"
          @fix-warning="$emit('fix-warning', $event)"
        >
          <template #publish-agreement="slotProps">
            <slot name="publish-agreement" v-bind="slotProps" />
          </template>
          <template #publish-details>
            <slot name="publish-details">
              <div>
                <strong>Name:</strong>
                {{ item_label || 'Unknown' }}
              </div>
              <div>
                <strong>Publication Time:</strong>
                {{ new Date().toLocaleString() }}
              </div>
            </slot>
          </template>
          <template #checklist-header="slotProps">
            <slot name="checklist-header" v-bind="slotProps" />
          </template>
          <template #checklist-items>
            <slot name="checklist-items" />
          </template>
        </widget-editor-publication-status>
      </slot>

      <v-row v-if="show_tabs">
        <v-col cols="12">
          <v-btn-toggle v-model="internal_active_tab" mandatory class="mb-4 full-width">
            <v-btn :value="0" class="px-5 flex-grow-1">
              <v-icon left color="secondary" aria-hidden="true">mdi-cog-outline</v-icon>
              General Settings
            </v-btn>
            <v-btn :value="1" class="px-5 flex-grow-1">
              <v-icon left color="secondary" aria-hidden="true">mdi-palette-outline</v-icon>
              Content & Styles
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <div v-show="!show_tabs || internal_active_tab === 0">
        <slot name="general-settings" />
      </div>

      <div v-show="!show_tabs || internal_active_tab === 1">
        <slot name="content-styles" />
      </div>

      <slot name="settings" />
    </div>

    <div class="preview-pane" :class="{ 'full-screen': settings_collapsed }">
      <slot name="preview" />
    </div>

    <qr-code-dialog ref="qrCodeDialog" />

    <v-dialog v-model="archive_dialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-archive-arrow-down-outline</v-icon>
          {{ archive_dialog_title }}
        </v-card-title>
        <v-card-text>
          <p class="body-1 mt-4">
            You are about to archive:
            <strong>{{ item_label || 'Unknown' }}</strong>
          </p>
          <v-alert color="primary" dense text class="mb-4">
            <ul class="pl-5 mb-0">
              <li>Archiving will take place immediately.</li>
              <li>This will archive both the draft and any published version.</li>
              <li>The {{ item_type }} will no longer be publicly accessible.</li>
            </ul>
          </v-alert>
          <p>
            Are you sure you want to proceed?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="archive_dialog = false" :disabled="archive_loading">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmArchive"
            :loading="archive_loading"
            :disabled="archive_loading"
          >
            <v-icon left>mdi-archive</v-icon>
            Archive {{ item_type }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QrCodeDialog from '@/components/input/qr-code-dialog.vue';
import WidgetEditorActionButtons from './widget-editor-action-buttons.vue';
import WidgetEditorPublicationStatus from './widget-editor-publication-status.vue';

/**
 * Reusable split-pane layout for widget editor screens.
 * Provides collapsible settings pane with tabs, preview pane, and standard header actions.
 *
 * To warn organization admins when leaving with unpublished changes, call the
 * confirmLeaveWithUnpublishedChanges method from your parent view's beforeRouteLeave guard.
 * Only works when used by a routed component (i.e. directly rendered by Vue Router)
 *
 * @example
 * // In your view component:
 * beforeRouteLeave(to, from, next) {
 *   this.$refs.widgetLayout.confirmLeaveWithUnpublishedChanges(next);
 * }
 *
 * <widget-editor-layout
 *   ref="widgetLayout"
 *   :settings_collapsed="fullscreen"
 *   :show_tabs="true"
 *   :active_tab.sync="active_tab"
 *   :back_route="back_route"
 *   back_label="Back to Forms"
 *   :is_published="is_published"
 *   :is_archived="form.is_archived"
 *   :can_unarchive="can_unarchive"
 *   :item_label="form.internal_label"
 *   item_type="form"
 *   :has_draft_changes="has_draft_changes"
 *   @copy-url="handleCopyUrl"
 *   @generate-qr="handleGenerateQR"
 *   @archive="handleArchive"
 *   @unarchive="handleUnarchive"
 * >
 *   <template #publication-status>
 *     <widget-editor-publication-status ... />
 *   </template>
 *   <template #general-settings>
 *     <v-card>Form settings...</v-card>
 *   </template>
 *   <template #content-styles>
 *     <v-card>Theme settings...</v-card>
 *   </template>
 *   <template #preview>
 *     <widget-preview-panel ... />
 *   </template>
 * </widget-editor-layout>
 */
export default {
  name: 'WidgetEditorLayout',

  components: {
    QrCodeDialog,
    WidgetEditorActionButtons,
    WidgetEditorPublicationStatus,
  },

  props: {
    /**
     * Whether settings pane is collapsed (fullscreen preview)
     */
    settings_collapsed: {
      type: Boolean,
      default: false,
    },

    /**
     * Width of settings pane (px)
     */
    settings_width: {
      type: Number,
      default: 720,
    },

    /**
     * Show General Settings / Content & Styles tabs
     */
    show_tabs: {
      type: Boolean,
      default: true,
    },

    /**
     * Currently active tab (0 or 1) - supports .sync
     */
    active_tab: {
      type: Number,
      default: 0,
    },

    /**
     * Route for back button
     */
    back_route: {
      type: [String, Object],
      default: null,
    },

    /**
     * Label for back button
     */
    back_label: {
      type: String,
      default: 'Back',
    },

    /**
     * Whether item is published (controls Copy URL/QR Code buttons)
     */
    is_published: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether item is in the archived state
     */
    is_archived: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether user has sufficient permissions to unarchive
     */
    can_unarchive: {
      type: Boolean,
      default: true,
    },

    /**
     * Show Copy URL button
     */
    show_copy_url: {
      type: Boolean,
      default: true,
    },

    /**
     * Show QR Code button
     */
    show_qr_code: {
      type: Boolean,
      default: true,
    },

    /**
     * Show Archive/Unarchive buttons
     */
    show_archive: {
      type: Boolean,
      default: true,
    },

    /**
     * Label of item being archived (for dialog)
     */
    item_label: {
      type: String,
      default: '',
    },

    /**
     * Type of item (e.g., 'form', 'widget') - used in dialog text
     */
    item_type: {
      type: String,
      default: 'form',
    },

    /**
     * Title for archive dialog
     */
    archive_dialog_title: {
      type: String,
      default: 'Archive Item',
    },

    /**
     * Tooltip text shown when user cannot unarchive
     */
    unarchive_tooltip: {
      type: String,
      default: 'Only organization administrators can unarchive forms.',
    },

    /**
     * Whether item has unpublished draft changes
     */
    has_draft_changes: {
      type: Boolean,
      default: false,
    },

    /**
     * Timestamp when item was last published
     */
    publish_time: {
      type: String,
      default: null,
    },

    /**
     * Digital signature ID from last publish
     */
    publish_signature_id: {
      type: String,
      default: null,
    },

    /**
     * Array of change descriptions for unpublished changes
     */
    change_descriptions: {
      type: Array,
      default: () => [],
    },

    /**
     * Array of validation warnings preventing publish
     */
    validation_warnings: {
      type: Array,
      default: () => [],
    },

    /**
     * Title for publish dialog
     */
    publish_dialog_title: {
      type: String,
      default: 'Publish Item',
    },

    /**
     * Whether to include setup checklist below the publication status expansion panel.
     * If false, will still show pre-publish warnings.
     */
    include_setup_checklist: {
      type: Boolean,
      default: false,
    },

    /**
     * Number of incomplete checklist steps (for badge display)
     * Parent component should calculate this based on their specific checklist
     */
    outstanding_steps_count: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      internal_active_tab: this.active_tab,
      archive_dialog: false,
      archive_loading: false,
    };
  },

  watch: {
    active_tab(newVal) {
      this.internal_active_tab = newVal;
    },

    internal_active_tab(newVal) {
      this.$emit('update:active_tab', newVal);
    },
  },

  methods: {
    confirmLeaveWithUnpublishedChanges(next) {
      if (this.$rc.$state.isOrganizationAdmin() && this.has_draft_changes) {
        this.$rc.confirm({
          title: 'Unpublished Changes',
          body: `You have unpublished changes that won't be visible to users until published. Are you sure you want to leave without publishing your changes?`,
          btn_text_ok: 'Leave Without Publishing',
          btn_text_cancel: 'Stay on Page',
          on_ok: () => next(),
          on_cancel: () => next(false),
        });
      } else {
        next();
      }
    },

    handleCopyUrl() {
      this.$emit('copy-url');
    },

    handleGenerateQR() {
      this.$emit('generate-qr', this.$refs.qrCodeDialog);
    },

    handleShowArchiveDialog() {
      this.archive_dialog = true;
    },

    confirmArchive() {
      this.archive_loading = true;
      this.$emit('archive', {
        onSuccess: () => {
          this.archive_loading = false;
          this.archive_dialog = false;
        },
        onError: () => {
          this.archive_loading = false;
          this.archive_dialog = false;
        },
      });
    },

    handleUnarchive() {
      this.$emit('unarchive');
    },

    closeSetupChecklist() {
      if (this.$refs.publicationStatus) {
        this.$refs.publicationStatus.closeSetupChecklist();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.widget-editor-layout {
  display: flex;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 15px;
  gap: 10px;
}

.settings-pane {
  flex: 0 0 v-bind('`${settings_width}px`');
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 15px;
  padding-left: 15px;
  padding-bottom: 15px;
  scroll-padding-top: 150px;
  box-shadow: 5px 0 5px -5px rgba(0, 0, 0, 0.2);
}

.preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding-left: 15px;
  padding-right: 15px;
  padding-bottom: 15px;
  overflow: auto;

  &.full-screen {
    padding-left: 0;
  }
}
</style>
